import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import type { InputProps } from "@mui/joy/Input";
import Typography from "@mui/joy/Typography";

const inputSx = {
    borderRadius: "4px",
    boxShadow: "none",
    bgcolor: "background.surface",
} as const;

type SharedFieldChromeProps = {
    label?: string;
    helperText?: string;
    errorMessage?: string;
    size?: "sm" | "md" | "lg";
};

/** Controlled input — no react-hook-form. Use with local state or any controlled form. */
export type ControlledTextFieldProps = SharedFieldChromeProps &
    Omit<InputProps, "onChange" | "value" | "size"> & {
        value: string;
        onChange?: (value: string) => void;
    };

export function ControlledTextField({
    label,
    helperText,
    errorMessage,
    value,
    onChange,
    size = "sm",
    sx,
    ...props
}: ControlledTextFieldProps) {
    const input = (
        <Input
            size={size}
            variant="outlined"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            {...props}
            sx={{
                ...inputSx,
                ...(sx as object),
            }}
        />
    );

    if (!label && !helperText && !errorMessage) {
        return input;
    }

    return (
        <FormControl error={!!errorMessage} size={size} sx={{ width: "100%" }}>
            {label && (
                <FormLabel sx={{ textTransform: "uppercase", fontSize: "12px", fontWeight: "xl", letterSpacing: "0.5px" }}>
                    {label}
                </FormLabel>
            )}
            {helperText && !errorMessage && (
                <Typography level="body-xs" sx={{ color: "text.secondary", mb: 1, mt: -0.7 }}>
                    {helperText}
                </Typography>
            )}
            {errorMessage && (
                <Typography level="body-xs" color="danger" sx={{ mb: 1, mt: -0.7 }}>
                    {errorMessage}
                </Typography>
            )}
            {input}
        </FormControl>
    );
}

interface FormTextFieldProps {
    name: string;
    label: string;
    placeholder?: string;
    rules?: object;
    type?: string;
    helperText?: string;
    startDecorator?: React.ReactNode;
    endDecorator?: React.ReactNode;
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    sx?: any;
}

/** react-hook-form bound field — requires FormProvider. */
const TextField: React.FC<FormTextFieldProps> = ({
    name,
    label,
    placeholder,
    rules = {},
    type = "text",
    helperText,
    startDecorator,
    endDecorator,
    size = "md",
    disabled,
    sx,
    ...props
}) => {
    const { control } = useFormContext();
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormControl error={!!fieldState.error} size={size}>
                    {label && (
                        <FormLabel sx={{ textTransform: "uppercase", fontSize: "12px", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            {label}
                        </FormLabel>
                    )}
                    {helperText && !fieldState.error && (
                        <Typography level="body-xs" sx={{ color: "text.secondary", mb: 1, mt: -0.7 }}>
                            {helperText}
                        </Typography>
                    )}
                    {fieldState.error && (
                        <Typography level="body-xs" color="danger" sx={{ mb: 1, mt: -0.7 }}>
                            {fieldState.error.message}
                        </Typography>
                    )}
                    <Input
                        {...field}
                        placeholder={placeholder}
                        type={type}
                        disabled={disabled}
                        startDecorator={startDecorator}
                        endDecorator={endDecorator}
                        {...props}
                        sx={{
                            ...inputSx,
                            ...((props as any).sx || {}),
                            ...sx,
                        }}
                    />
                </FormControl>
            )}
        />
    );
};

export default TextField;
